import { apiSlice } from "api/api.slice";
import { QUERY_LIMIT } from "api/constants";
import type { SourceDetail } from "api/schemas/sourcesApiSchemas";
import { getSourcesSchema, sourceDetailSchema } from "api/schemas/sourcesApiSchemas";

type GetSourcesRequestParams = {
  searchQuery?: string;
  filters?: {
    status: boolean | null;
    type: string | null;
    publicationType: string | null;
    contentType: string | null;
    license: string | null;
    countries: string[];
    regions: string[];
    greaterRegions: string[];
  };
};

type CreateSourcePayload = {
  name: string;
  url: string;
  description: string;
  active: boolean;
  publicationType: string;
  logoUrl?: string;
  license?: string;
  autopilot?: string | null;
  type?: string;
  contentType?: string;
  country?: string;
  region?: string;
  greaterRegion?: string;
};

type UpdateSourcePayload = {
  sourceId: string;
  name: string;
  url: string;
  description: string;
  active: boolean;
  logoUrl?: string;
  license?: string;
  autopilot?: string | null;
  type?: string;
  contentType?: string;
  publicationType: string;
  country?: string;
  region?: string;
  greaterRegion?: string;
};

export const sourcesApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getSources: build.infiniteQuery<SourceDetail[], GetSourcesRequestParams, number>({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.length < QUERY_LIMIT) return undefined;

          return lastPageParam + 1;
        },
      },
      query: ({ pageParam, queryArg: { searchQuery, filters } }) => {
        const offset = (pageParam - 1) * QUERY_LIMIT;

        const getFilterParams = () => {
          if (!filters) return {};

          const { status, type, publicationType, contentType, license, countries, regions, greaterRegions } = filters;

          return {
            ...(status !== null ? { active: status } : {}),
            ...(type !== null ? { type } : {}),
            ...(publicationType !== null ? { publication_type: publicationType } : {}),
            ...(contentType !== null ? { content_type: contentType } : {}),
            ...(license ? { license } : {}),
            ...(countries.length > 0 ? { countries } : {}),
            ...(regions.length > 0 ? { regions } : {}),
            ...(greaterRegions.length > 0 ? { greater_regions: greaterRegions } : {}),
          };
        };

        return {
          method: "GET",
          url: `/source`,
          schema: getSourcesSchema,
          params: {
            name: searchQuery,
            limit: QUERY_LIMIT,
            offset,
            ...getFilterParams(),
          },
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.pages.flat().map(({ id }) => ({ type: "Sources" as const, id })),
              { type: "Sources", id: "LIST" },
            ]
          : [{ type: "Sources", id: "LIST" }],
    }),
    getSourceById: build.query<SourceDetail, { sourceId: string }>({
      query: ({ sourceId }) => ({
        method: "GET",
        url: `/source/${sourceId}`,
        schema: sourceDetailSchema,
      }),
      providesTags: (_result, _error, args) => [{ type: "Sources", id: args.sourceId }],
    }),
    createSource: build.mutation<{ id: string }, CreateSourcePayload>({
      query: ({
        name,
        url,
        description,
        logoUrl,
        active,
        license,
        autopilot,
        type,
        contentType,
        publicationType,
        country,
        region,
        greaterRegion,
      }) => ({
        method: "POST",
        url: `/source`,
        body: {
          name,
          url,
          description,
          active,
          favicon: logoUrl,
          license,
          auto_approved: autopilot ?? null,
          type,
          content_type: contentType,
          publication_type: publicationType,
          country,
          region,
          greater_region: greaterRegion,
        },
      }),
    }),
    uploadSourceImage: build.mutation<{ favicon: string }, { file: File }>({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append("image", file);
        formData.append("object_type", "source");

        return { method: "POST", url: `/image`, body: formData };
      },
    }),
    updateSource: build.mutation<undefined, UpdateSourcePayload>({
      query: ({
        sourceId,
        name,
        url,
        description,
        logoUrl,
        active,
        license,
        autopilot,
        type,
        contentType,
        publicationType,
        country,
        region,
        greaterRegion,
      }) => ({
        method: "PATCH",
        url: `/source/${sourceId}`,
        body: {
          name,
          url,
          description,
          active,
          favicon: logoUrl,
          license,
          auto_approved: autopilot ?? null,
          type,
          content_type: contentType,
          publication_type: publicationType,
          country,
          region,
          greater_region: greaterRegion,
        },
      }),
      invalidatesTags: (_result, _error, { sourceId }) => [{ type: "Sources", id: sourceId }],
    }),
    deleteSource: build.mutation<undefined, { sourceId: string }>({
      query: ({ sourceId }) => ({
        method: "DELETE",
        url: `/source/${sourceId}`,
      }),
      invalidatesTags: (_result, _error, { sourceId }) => [
        { type: "Sources", id: sourceId },
        { type: "Sources", id: "LIST" },
      ],
    }),
  }),
});
